"use client";

import { memo, useMemo } from "react";
import { useController, useFormContext, useWatch } from "react-hook-form";
import { HmcTab, HmcTabPanel } from "@/components/hmc-on-server";
import { t } from "@/utils";
import { ContentEditor, useTabs } from "@/components";
import { HmcMessage } from "@adi-ctx/harmonic-core-components-react";
import type { TElement } from "@udecode/plate-common";

type FormTabProps = {
	readonly hasFieldError: (field: string) => boolean;
	readonly isDisabledIf?: { field: string; value: any }; // disable section if the provided field value is true
};

/**
 * Main content panel + content editor form field
 * @constructor
 */
function FormTabContent(props: FormTabProps) {
	const { selectedTab, setSelectedTab } = useTabs();
	const { control, getValues, formState } = useFormContext();
	const isDisabledWatch = useWatch({
		control,
		// @ts-expect-error "useWatch" cannot be declared conditionally and "name" here is required by type, "isDisabledIf" may not exist
		name: props.isDisabledIf?.field,
	});

	const isDisabled = useMemo(
		() =>
			props.isDisabledIf
				? isDisabledWatch === props.isDisabledIf?.value
				: false,
		[isDisabledWatch, props]
	);

	const { field: content } = useController({
		control,
		name: "content",
		rules: { required: !isDisabled },
	});

	return (
		<>
			<HmcTab
				isDisabled={isDisabled}
				isSelected={selectedTab === "panel-content"}
				panel="panel-content"
				onTabClicked={(e: { detail: string }) => {
					setSelectedTab(e.detail);
				}}
			>
				{t("pages.cms.tab-content")}
				{!isDisabled && !getValues("content") && formState.isSubmitted && (
					<sup className="c-system-danger">*</sup>
				)}
			</HmcTab>
			<div
				className={`mt-6 w-full flex-col overflow-y-hidden ${selectedTab === "panel-content" ? "flex" : "hidden"}`}
				slot="panel"
			>
				{!isDisabled && (
					<div className="grow bg-white px-6 py-8">
						<ContentEditor
							value={content.value}
							onChange={(value: TElement[] | "") => {
								content.onChange(value);
							}}
						/>
						{props.hasFieldError("content") && (
							<HmcMessage
								status="error"
								iconName="error-s"
								className="mt-1 block"
								data-testid="cms-form-content-hint"
							>
								{t("pages.cms.required-field", {
									field: t("pages.cms.tab-content"),
								})}
							</HmcMessage>
						)}
					</div>
				)}
			</div>
		</>
	);
}

export default memo(FormTabContent);
